import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Flame, Trophy, Gift, Radar, Users, Share2, CheckCircle2, Clock } from "lucide-react";
import { trackEvent } from "@/lib/audit";
import type { EventType } from "@/types";
import { useLanguage } from "@/hooks/useLanguage";

const ev = (name: string) => name as unknown as EventType;

function localeFromLang(lang: "ES" | "EN" | "PT"): string {
  return lang === "EN" ? "en-US" : lang === "PT" ? "pt-BR" : "es-MX";
}

/**
 * PR-25 — World Cup Conversion Blitz.
 * Pure UI/presentation. Does NOT touch auth, predictions, Nation Wars,
 * rankings, XP or backend. All metrics are simulated when no live data
 * stream is available, and animate visibly to reinforce urgency.
 */

/* ───────────── 1. HERO ───────────── */
export function HeroBlitz() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  useEffect(() => {
    void trackEvent(ev("landing_view"), { surface: "welcome_blitz" });
  }, []);

  const onPrimary = () => {
    void trackEvent(ev("cta_click"), { id: "empezar_gratis" });
    void trackEvent(ev("signup_started"), { source: "hero_blitz" });
    void navigate({ to: "/registro" });
  };

  const features = [
    { icon: Flame, label: t("wcb_feature_live_predictions") },
    { icon: Trophy, label: t("wcb_feature_world_ranking") },
    { icon: Gift, label: t("wcb_feature_daily_rewards") },
    { icon: Radar, label: t("wcb_feature_tactical_radar") },
  ];

  return (
    <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-[oklch(0.18_0.04_270)] to-[oklch(0.08_0.02_270)] p-4 sm:p-8">
      <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
      <p className="text-[10px] font-black uppercase tracking-[0.25em] text-primary">
        {t("wcb_hero_eyebrow")}
      </p>
      <h1 className="mt-2 text-2xl font-black leading-[1.05] text-foreground sm:text-5xl">
        {t("wcb_hero_title_a")}{" "}
        <span className="bg-gradient-to-r from-primary to-[oklch(0.75_0.2_25)] bg-clip-text text-transparent">
          {t("wcb_hero_title_b")}
        </span>
      </h1>
      <p className="mt-2 max-w-xl text-[13px] text-muted-foreground sm:text-base">
        {t("wcb_hero_sub")}
      </p>

      <ul className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {features.map(({ icon: Icon, label }) => (
          <li
            key={label}
            className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-bold text-foreground sm:text-xs"
          >
            <Icon className="h-4 w-4 shrink-0 text-primary" />
            <span className="min-w-0 truncate">{label}</span>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onPrimary}
        className="mt-4 w-full rounded-2xl bg-[oklch(0.62_0.24_25)] px-6 py-3.5 text-base font-black uppercase tracking-wider text-white shadow-[0_10px_30px_-10px_oklch(0.62_0.24_25/0.7)] transition-transform hover:scale-[1.02] sm:py-4 sm:text-lg"
      >
        {t("wcb_cta_start_free")}
      </button>
      <p className="mt-2 text-center text-[10px] text-muted-foreground">
        {t("wcb_cta_disclaimer")}
      </p>
    </section>
  );
}

/* ───────────── 2. URGENCY COUNTDOWN ───────────── */
export function UrgencyCountdown() {
  const { t } = useLanguage();
  const [sec, setSec] = useState(58);
  useEffect(() => {
    const id = setInterval(() => {
      setSec((s) => (s <= 1 ? 60 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const mm = String(Math.floor(sec / 60)).padStart(2, "0");
  const ss = String(sec % 60).padStart(2, "0");

  return (
    <div
      role="timer"
      aria-live="polite"
      className="flex items-center justify-between gap-3 rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Clock className="h-4 w-4 shrink-0 animate-pulse text-primary" />
        <span className="min-w-0 truncate text-xs font-bold text-foreground sm:text-sm">
          {t("wcb_countdown_label")}
        </span>
      </div>
      <span className="shrink-0 font-mono text-xl font-black tabular-nums text-primary sm:text-2xl">
        {mm}:{ss}
      </span>
    </div>
  );
}

/* ───────────── 3. LIVE ACTIVITY BAR ───────────── */
export function LiveActivityBar() {
  const { t, lang } = useLanguage();
  // P0-A hydration fix: SSR and first client render MUST produce the same
  // markup. Math.random() at render time creates a mismatch (React error
  // #418) and forces a full client re-render that tanks LCP. We render a
  // deterministic baseline first, then jitter only after mount.
  const SSR_BASELINE = 2000;
  const [count, setCount] = useState(SSR_BASELINE);
  useEffect(() => {
    setCount(1800 + Math.floor(Math.random() * 900));
    const id = setInterval(() => {
      setCount(1200 + Math.floor(Math.random() * 2300));
    }, 20000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="flex items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-bold text-foreground">
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <span className="tabular-nums">{count.toLocaleString(localeFromLang(lang))}</span>
      <span className="text-muted-foreground">{t("wcb_live_fans_playing")}</span>
    </div>
  );
}

/* ───────────── 5. SOCIAL PROOF POPUP (P0-02 visual fix) ─────────────
 * The previous version showed a hardcoded demo ticker with fake nations
 * (Carlos/MX, María/AR, Javier/ES, etc.) that created confusion about
 * real Nation Wars activity. Per launch audit P0-02, the ticker is
 * disabled until it can be wired to real nation_score_contributions
 * data. Component is kept exported as a no-op to avoid breaking
 * existing imports (welcome.tsx).
 */
export function SocialProofPopup() {
  return null;
}

/* ───────────── 6. REFERRAL BLOCK ───────────── */
export function ReferralBlock() {
  const { t } = useLanguage();
  const onShare = useCallback(() => {
    void trackEvent(ev("referral_click"), { channel: "whatsapp", surface: "welcome_blitz" });
    const text = t("wcb_referral_share_text");
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  }, [t]);
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
          <Users className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-black text-foreground">{t("wcb_referral_title")}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("wcb_referral_sub")}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onShare}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-black text-white shadow"
      >
        <Share2 className="h-4 w-4" />
        {t("wcb_referral_share_button")}
      </button>
    </section>
  );
}

/* ───────────── 8. TRUST BLOCK ───────────── */
export function TrustBlock() {
  const { t } = useLanguage();
  const items = [
    t("wcb_trust_free_start"),
    t("wcb_trust_no_card"),
    t("wcb_trust_realtime"),
    t("wcb_trust_global"),
  ];
  return (
    <ul className="grid grid-cols-2 gap-2">
      {items.map((it) => (
        <li
          key={it}
          className="flex items-center gap-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-3 py-2 text-[11px] font-bold text-foreground sm:text-xs"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
          <span className="min-w-0">{it}</span>
        </li>
      ))}
    </ul>
  );
}